import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  CircularProgress,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import { coursesAPI, lessonsAPI, testsAPI } from '../services/api';
import { saveLesson } from '../utils/lessonUtils';
import { toast } from 'react-toastify';
import Button from '../components/UIKit/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import LessonEditor from '../components/UIKit/LessonEditor';
import JsonPartExport from '../components/UIKit/JsonPartExport';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProfessions, selectAllProfessions, selectProfessionsLoading } from '../redux/slices/professionsSlice';
import { fetchSkills, selectAllSkills, selectSkillsLoading } from '../redux/slices/skillsSlice';

const EditLessonPage = () => {
  const { courseSlug, moduleSlug, lessonSlug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation(['lessons', 'common']);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [lesson, setLesson] = useState(null);

  // Redux селекторы для профессий и навыков
  const professions = useSelector(selectAllProfessions);
  const professionsLoading = useSelector(selectProfessionsLoading);
  const skills = useSelector(selectAllSkills);
  const skillsLoading = useSelector(selectSkillsLoading);

  // Проверяем, является ли пользователь админом или учителем
  const isAdmin = user?.userMeta?.roles?.includes('admin');
  const isTeacher = user?.userMeta?.roles?.includes('teacher');
  const isAdminOrTeacher = isAdmin || isTeacher;

  // Проверяем доступ
  useEffect(() => {
    if (!user) return;
    
    if (!isAdminOrTeacher) {
      setError(t('common:errors.accessDenied'));
      setTimeout(() => {
        if (courseSlug && moduleSlug && lessonSlug) {
          navigate(`/courses/${courseSlug}/modules/${moduleSlug}/lessons/${lessonSlug}`);
        } else {
          navigate('/lessons/my');
        }
      }, 3000);
    }
  }, [user, courseSlug, moduleSlug, lessonSlug, navigate, t, isAdminOrTeacher]);

  // Загружаем профессии и навыки при монтировании компонента
  useEffect(() => {
    if (professions.length === 0 && !professionsLoading) {
      dispatch(fetchProfessions({ all: true, isCommon: 1 }));
    }
    if (skills.length === 0 && !skillsLoading) {
      dispatch(fetchSkills({ all: true, isCommon: 1 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Оптимизированная загрузка данных урока
  useEffect(() => {
    let isMounted = true;

    const fetchLesson = async () => {
      try {
        setLoading(true);
        setError(null);

        let response;
        
        // Если урок редактируется в контексте курса
        if (courseSlug && moduleSlug && lessonSlug) {
          response = await coursesAPI.getLesson(
            courseSlug,
            moduleSlug,
            lessonSlug
          );
          
          if (!isMounted) return;
          setLesson(response.data.lesson);
        } else if (lessonSlug) {
          // Если урок редактируется вне контекста курса
          response = await lessonsAPI.getById(lessonSlug);
          
          if (!isMounted) return;
          
          if (response?.data?.data) {
            const lessonData = response.data.data;
            setLesson(lessonData);
            
            // Проверка прав доступа
            const isAuthor = lessonData.instructor?._id === user?._id;
            if (!isAdmin && !isTeacher && !isAuthor) {
              setError(t('common:errors.accessDenied'));
              setTimeout(() => {
                if (isMounted) {
                  navigate(`/lessons/${lessonSlug}`);
                }
              }, 3000);
            }
          } else {
            setError(t('page:errors.notFound'));
          }
        } else {
          throw new Error('Не удалось определить контекст редактирования урока');
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('Ошибка при загрузке урока:', err);
        setError(
          err.response?.data?.message || t('common:errors.loadingFailed')
        );
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchLesson();

    // Cleanup функция для предотвращения утечек памяти
    return () => {
      isMounted = false;
    };
  }, [courseSlug, moduleSlug, lessonSlug, user, navigate, isAdmin, isTeacher, t]);

  const handleSaveLesson = async () => {
    try {
      setSaving(true);

      // Валидация обязательных полей
      if (!lesson?.title?.trim()) {
        toast.error(t('create:validation.titleRequired'));
        return;
      }

      // Обычное сохранение урока с валидацией
      const validatedLesson = {
        ...lesson,
        title: lesson.title.trim(),
        content: lesson.content || '',
        contentBlocks: lesson.contentBlocks || [],
        resources: lesson.resources || [],
        practiceExercises: lesson.practiceExercises || [],
        professions: lesson.professions || [],
        tests: lesson.tests || []
      };
      
      await saveLesson(validatedLesson);
      
      // Если есть тесты, обновляем их по одному
      if (lesson.tests && lesson.tests.length > 0) {
        try {
          for (const testData of lesson.tests) {
            if (testData._id && !testData._id.startsWith('temp-')) {
              // Обновляем существующий тест с lessonId
              await testsAPI.update(testData._id, { ...testData, lessonId: lesson._id });
              console.log('Тест обновлен:', testData._id);
            } else {
              const { _id, ...testDataWithoutId } = testData;
              await testsAPI.create({ ...testDataWithoutId, lessonId: lesson._id });
              console.log('Тест создан для урока:', lesson._id);
            }
          }
          console.log('Все тесты успешно обработаны для урока:', lesson._id);
        } catch (testError) {
          console.error('Ошибка при обновлении тестов:', testError);
          toast.warning('Урок сохранен, но произошла ошибка при обновлении тестов');
        }
      }

      toast.success(t('editLesson.saveSuccess'));
      navigate(courseSlug && moduleSlug ? `/courses/${courseSlug}/modules/${moduleSlug}/lessons/${lessonSlug}` : '/lessons/my');
    } catch (error) {
      console.error('Ошибка при сохранении урока:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          t('common:errors.savingFailed');
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleBackToLesson = () => {
    if (courseSlug && moduleSlug && lessonSlug) {
      navigate(`/courses/${courseSlug}/modules/${moduleSlug}/lessons/${lessonSlug}`);
    } else if (lessonSlug) {
      navigate(`/lessons/my`);
    } else {
      navigate('/lessons/my');
    }
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '300px',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!lesson) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="warning">{t('editLesson.lessonNotFound')}</Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
        }}
      >
        <Typography variant="h4" component="h1">
          {t('editLesson.title')}
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToLesson}
            sx={{ mr: 2 }}
          >
            {t('editLesson.buttons.back')}
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveLesson}
            disabled={saving}
          >
            {saving
              ? t('editLesson.buttons.saving')
              : t('editLesson.buttons.save')}
          </Button>
        </Box>
      </Box>

      <LessonEditor
        lesson={lesson}
        onChange={setLesson}
        isAdminOrTeacher={isAdminOrTeacher}
        header={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h5" component="h2">
              {lesson.title || t('editLesson.newLesson')}
            </Typography>
            <Box>
              <JsonPartExport
                data={lesson}
                title={lesson.title}
                type={t('courses:jsonExport.lesson', { defaultValue: 'lesson' })}
              />
            </Box>
          </Box>
        }
      />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mt: 4,
        }}
      >
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSaveLesson}
          disabled={saving}
          size="large"
        >
          {saving
            ? t('editLesson.buttons.saving')
            : t('editLesson.buttons.save')}
        </Button>
      </Box>
    </Container>
  );
};

export default EditLessonPage;
